import { Empty, Flex, IconFont, Image, Text } from '@byecode/ui'
import { getAssetUrl } from '@lighthouse/assets'
import type { DTFile, FileBlockAbstract } from '@lighthouse/core'
import {
    FILE_ICON_MAP,
    FileItem,
    FilePreviewer,
    flexMinSizeStyle,
    getCursorStyle,
    getFileNameByUrl,
    getFileSizeByUrl,
    getFileTypeByUrl,
    getImageFullUrlInApplication,
    useApplicationContext
} from '@lighthouse/shared'
import { BreakPointSize, getFileSizeWithUnit, nanoid, useBreakpoint } from '@lighthouse/tools'
import clsx from 'classnames'
import React, { useCallback, useMemo, useState } from 'react'
import { useClickAway } from 'react-use'
import styled from 'styled-components'

import { AnimationDecorators } from '../../components'
import { useEffectiveAnchorId } from '../../context'
import { useScrollToAnchorWhenHashMatch } from '../../hooks/useScrollToAnchorWhenHashMatch'

interface FileBlockProps extends React.ComponentPropsWithoutRef<'div'> {
    blockData: FileBlockAbstract
    value?: DTFile[]
}

const SCxContainer = styled(AnimationDecorators)`
    padding: var(--block-padding);
    height: 100%;
    width: 100%;
    overflow: hidden;
`

const SCxWrapper = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
`

const SCxFileContainer = styled.div`
    width: 100%;
    height: 100%;
    border-radius: 8px;
    border: 1px solid var(--color-gray-200);
    overflow: hidden;

    &.disabled {
        & ::before {
            position: absolute;
            content: '';
            inset: 0;
            z-index: 1;
        }
    }

    &.selected {
        /* box-shadow: 0 0 0 3px var(--color-gray-200); */
        /* border: 1px solid var(--color-gray-500); */
    }
`

const SCxCard = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    padding: 12px 8px;
    gap: 8px;
    background-color: var(--color-gray-100);
    border-radius: 8px;

    &.selected {
        box-shadow: 0 0 0 3px var(--color-gray-200);
        border: 1px solid var(--color-gray-500);
    }
`

const SCxTag = styled.div``

const FileBlock: React.FunctionComponent<FileBlockProps> = ({ blockData, value, style, ...rest }) => {
    const { id, title, config } = blockData
    const { previewMode, fileSource, breakPoint: configBreakPoint, cursor, anchor } = config
    const { anchorId } = anchor || {}
    const effectiveAnchorId = useEffectiveAnchorId(anchorId)
    useScrollToAnchorWhenHashMatch(effectiveAnchorId, anchor)
    const [opened, setOpened] = useState(false)
    const [selected, setSelected] = useState(false)
    const { appId = '' } = useApplicationContext()
    // const ref = useRef<HTMLDivElement>(null)
    const { ref, breakPoint } = useBreakpoint()
    const isMobile = BreakPointSize.sm === breakPoint || BreakPointSize.xs === breakPoint

    const modalId = nanoid()

    const fileUrl = useMemo(
        () =>
            value?.find(url => {
                const type = getFileTypeByUrl(url.split('?')[0])
                return type !== 'image' && type !== 'video'
            }),
        [value]
    )

    const file = useMemo(() => {
        if (!fileUrl) {
            return
        }
        return {
            name: getFileNameByUrl(fileUrl) || '',
            url: getImageFullUrlInApplication(appId, fileUrl),
            size: getFileSizeByUrl(fileUrl),
            type: getFileTypeByUrl(fileUrl)
        }
    }, [appId, fileUrl])

    useClickAway(ref, e => {
        if (e.target instanceof Element) {
            const modalEle = document.querySelector(`[data-filePreviewId = '${modalId}']`)
            if (e.target && modalEle?.contains(e.target)) {
                return
            }
            setSelected(false)
        }
    })

    const handlePreview = useCallback(() => {
        if (!file) {
            return
        }
        if (file.type === 'pdf') {
            return
        }
        setOpened(true)
    }, [file])

    const contentEle = useMemo(() => {
        if (!file) {
            return (
                <Empty
                    styles={{
                        wrapper: {
                            gap: 12
                        }
                    }}
                    icon={<Image height={140} fit="contain" src={getAssetUrl('empty', 'file_block_no_source_empty.png')} />}
                    description="请在「右侧栏>配置」中选择数文件来源"
                />
            )
        }
        if (previewMode === 'introduce') {
            return (
                <SCxCard className={clsx({ selected })}>
                    <SCxTag>
                        <IconFont type={FILE_ICON_MAP[file.type]} size={48} />{' '}
                    </SCxTag>
                    <Flex direction="column" gap={4}>
                        <Text size={12} color="var(--color-gray-600)" lineClamp={1} style={{ lineHeight: '24px' }} weight={500}>
                            {file.name}
                        </Text>
                        <Text size={12} color="var(--color-gray-400)" lineClamp={1} style={{ lineHeight: '20px' }}>
                            附件大小： {getFileSizeWithUnit(file.size)}
                        </Text>
                    </Flex>
                </SCxCard>
            )
        }
        return (
            <SCxFileContainer className={clsx({ selected, disabled: file.type !== 'ppt' && file.type !== 'audio' && file.type !== 'pdf' })}>
                <FileItem showPdfThumbs={!isMobile} prefix={`${id}_`} disabledSlider active file={file} />
            </SCxFileContainer>
        )
    }, [file, id, isMobile, previewMode, selected])

    return (
        <SCxContainer
            ref={ref}
            blockId={id}
            blockName={title}
            animation={configBreakPoint?.animation}
            id={effectiveAnchorId}
            style={{ ...flexMinSizeStyle, ...style, ...getCursorStyle(appId, cursor) }}
            {...rest}
        >
            <SCxWrapper onClick={() => setSelected(true)} onDoubleClick={handlePreview}>
                {contentEle}
            </SCxWrapper>
            <FilePreviewer
                withControls={false}
                fileList={file ? [file] : []}
                onClose={() => setOpened(false)}
                opened={opened}
                data-filePreviewId={modalId}
            />
        </SCxContainer>
    )
}

export default React.memo(FileBlock)
